import React from 'react';
import {
	RECITALS as recitals,
	CLOSURES as closures,
	SEASON_LABEL,
} from '../../data/seasonData';
import { googleCalendarUrl, buildIcs, downloadIcs } from '../../utils/calendarLinks';
import WaButton from '../../components/wa/WaButton';
import SeasonMonthGrid from './SeasonMonthGrid';
import CalendarSyncCard from './CalendarSyncCard';
import ViewToggle from '../../components/Portal/ViewToggle';
import { PageWrap, PageHead, Card, SectionHead } from './customerUi';

/* Season calendar — recitals, closures and the month grid.
 *
 * CONVERTED OFF REACT-BOOTSTRAP 2026-08-10 (portal review pass), because the
 * framework WAS the defect Anthony reported. His two items — "redesign the alert
 * banner for a cleaner look" and "adjust the visual style (e.g. blue borders on
 * recital cards) to align with the brand" — both traced to Bootstrap defaults
 * rather than to choices anyone made: `<Alert variant='info'>`, plus
 * `border='primary'`, `<Badge bg='primary'>` and `variant='outline-primary'`,
 * which is where the blue came from. Restyling those in place would have meant
 * overriding Bootstrap with CYV tokens; removing Bootstrap is the smaller change
 * and the one CLAUDE.md's "touch it, modernize it" asks for.
 *
 * TWO MOUNTS, AND THEY NEED DIFFERENT CHROME. This renders at the customer route
 * /portal/customer/calendar AND inside the staff Scheduling page's
 * "Important dates" tab. That tab already draws its own PortalHeader, so the
 * page's own <h1> was a SECOND heading inside it — an existing oddity, now
 * fixed by the explicit `embedded` prop rather than by guessing from `showFlex`
 * (which means "surface reserve/flex days" and has nothing to do with chrome).
 */

// Parse 'YYYY-MM-DD' as LOCAL midnight (avoids the UTC-shift that would show
// the previous day in a negative-offset time zone).
const parseLocal = (s) => {
	const [y, m, d] = s.split('-').map(Number);
	return new Date(y, m - 1, d);
};

const formatDate = (dateString) =>
	parseLocal(dateString).toLocaleDateString('en-US', {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});

// A closure range as human text, collapsing a single-day range.
const formatRange = (start, end) =>
	start === end ? formatDate(start) : `${formatDate(start)} – ${formatDate(end)}`;

const isUpcoming = (dateString) =>
	parseLocal(dateString) >= new Date(new Date().setHours(0, 0, 0, 0));

// "Add to Calendar" — season events carry a date only, so each is an all-day
// event. Google opens a prefilled event; the .ics serves Apple / Outlook.
const recitalEvent = (recital) => ({
	title: `${recital.name} — Color Your Voice`,
	date: recital.date,
	details: 'Color Your Voice recital. No regular lessons this day.',
	location: recital.location || '',
});

const Chip = ({ children, tone = 'muted' }) => {
	const palette = {
		teal: {
			bg: 'color-mix(in srgb, var(--cyv-teal-600, #54ada7) 15%, var(--surface-card, #fff))',
			fg: 'var(--cyv-teal-600, #2f7d77)',
		},
		muted: {
			bg: 'var(--psp-chip-track, rgba(0,0,0,.05))',
			fg: 'var(--text-muted, #7b8499)',
		},
	}[tone];
	return (
		<span
			style={{
				display: 'inline-block',
				flex: '0 0 auto',
				padding: '2px 9px',
				borderRadius: 999,
				fontSize: '0.68rem',
				fontWeight: 'var(--fw-bold, 700)',
				letterSpacing: '0.06em',
				textTransform: 'uppercase',
				background: palette.bg,
				color: palette.fg,
			}}>
			{children}
		</span>
	);
};

const rowLine = {
	display: 'flex',
	alignItems: 'baseline',
	gap: 8,
	fontSize: '0.84rem',
	color: 'var(--text-body, #4a5570)',
	lineHeight: 1.55,
};

const SeasonCalendar = ({ showFlex = false, embedded = false }) => {
	/* LIST IS THE DEFAULT, and the month grid is now the ALTERNATIVE view rather
	   than a permanent header (Anthony, 2026-08-20: "that calendar widget at the
	   top of the page feels out of place and like it doesn't serve a purpose").
	   He was right, and the reason is duplication: the grid rendered exactly the
	   recitals and closures that the two cards below it already listed, so the
	   page said everything twice and led with the less readable copy. A month
	   grid earns its place when it holds something the list cannot show at a
	   glance — which is why it stays reachable rather than being deleted.

	   STAFF MOUNT IS DELIBERATELY UNCHANGED. The embedded Scheduling → Important
	   dates tab keeps the grid stacked above the lists exactly as before: the
	   same duplication exists there, but nobody asked for it to change and
	   CLAUDE.md's "don't break existing employee tooling" outranks tidiness on a
	   surface that is not the one under review. */
	const [view, setView] = React.useState('list');
	const showGrid = embedded || view === 'calendar';
	const showLists = embedded || view === 'list';

	const body = (
		<>
			{/* The old Bootstrap info Alert, rewritten as a quiet note. A parent
			    reading their season calendar is not being warned about anything —
			    a full-width blue alert box overstated it. */}
			<Card style={{ marginBottom: 14 }}>
				<div style={{ display: 'flex', gap: 11, padding: '13px 15px' }}>
					<i
						className='fa-sharp fa-solid fa-circle-info'
						style={{
							fontSize: 14,
							marginTop: 2,
							color: 'var(--cyv-teal-600, #2f7d77)',
							flex: '0 0 auto',
						}}
						aria-hidden='true'
					/>
					<p
						style={{
							margin: 0,
							fontSize: '0.85rem',
							lineHeight: 1.6,
							color: 'var(--text-body, #4a5570)',
						}}>
						Lessons follow the studio season below. There are no lessons on the
						closures and recital days listed here, and planned closures don’t earn
						a makeup credit.
					</p>
				</div>
			</Card>

			{/* The alternative view. READ-ONLY by construction — SeasonMonthGrid
			    renders season dates and exposes no affordance that writes; a day
			    cell must never become a second path into timeSlots. */}
			{showGrid && <SeasonMonthGrid showFlex={showFlex} />}

			{showLists && (<Card style={{ marginTop: 14, marginBottom: 14 }}>
				<SectionHead icon='fa-star' label='Recitals' />
				{recitals.map((recital, i) => (
					<div
						key={recital.date + recital.name}
						style={{
							padding: '4px 15px 15px',
							borderBottom:
								i < recitals.length - 1
									? '1px solid var(--cyv-line, #f0eae1)'
									: 'none',
							marginBottom: i < recitals.length - 1 ? 11 : 0,
						}}>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 8,
								marginBottom: 6,
								flexWrap: 'wrap',
							}}>
							<span
								style={{
									fontSize: '0.98rem',
									fontWeight: 'var(--fw-bold, 700)',
									color: 'var(--text-heading, #384971)',
								}}>
								{recital.name}
							</span>
							{isUpcoming(recital.date) && <Chip tone='teal'>Upcoming</Chip>}
						</div>

						<div style={rowLine}>
							<i
								className='fa-sharp fa-solid fa-calendar'
								style={{ color: 'var(--text-muted, #9aa2b1)', fontSize: 12 }}
								aria-hidden='true'
							/>
							<span>{formatDate(recital.date)}</span>
						</div>
						{recital.location && (
							<div style={rowLine}>
								<i
									className='fa-sharp fa-solid fa-location-dot'
									style={{ color: 'var(--text-muted, #9aa2b1)', fontSize: 12 }}
									aria-hidden='true'
								/>
								<span>{recital.location}</span>
							</div>
						)}
						<div style={{ ...rowLine, color: 'var(--text-muted, #7b8499)' }}>
							<i
								className='fa-sharp fa-solid fa-calendar-xmark'
								style={{ color: 'var(--text-muted, #9aa2b1)', fontSize: 12 }}
								aria-hidden='true'
							/>
							<span>No regular lessons on recital day.</span>
						</div>

						<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 11 }}>
							<WaButton
								size='small'
								appearance='outlined'
								href={googleCalendarUrl(recitalEvent(recital))}
								target='_blank'
								rel='noopener noreferrer'>
								<i slot='start' className='fa-sharp fa-solid fa-calendar-plus'></i>
								Google Calendar
							</WaButton>
							<WaButton
								size='small'
								appearance='outlined'
								onClick={() =>
									downloadIcs(
										`${recital.name.replace(/\s+/g, '-').toLowerCase()}-cyv`,
										buildIcs(recitalEvent(recital))
									)
								}>
								<i slot='start' className='fa-sharp fa-solid fa-calendar-plus'></i>
								Apple / Outlook
							</WaButton>
						</div>
					</div>
				))}
			</Card>)}

			{showLists && (<Card>
				<SectionHead icon='fa-calendar-xmark' label='Studio closures' />
				<p
					style={{
						margin: '0 15px 4px',
						fontSize: '0.8rem',
						color: 'var(--text-muted, #7b8499)',
						lineHeight: 1.55,
					}}>
					The studio is closed for these holidays and breaks. Planned closures
					don’t earn a makeup credit.
				</p>
				{/* ONLY PAST closures carry a chip. Badging every row "Upcoming"
				    repeated the same word a dozen times down a fixed left column —
				    no information, and at 375px it squeezed the actual dates into a
				    narrow gutter. The common state is the one that needs no label;
				    the exception gets the marker, and is dimmed besides. */}
				{closures.map((closure) => {
					const past = !isUpcoming(closure.end);
					return (
						<div
							key={closure.start + closure.name}
							style={{
								padding: '11px 15px',
								borderTop: '1px solid var(--cyv-line, #f0eae1)',
								opacity: past ? 0.55 : 1,
							}}>
							<span
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: 8,
									flexWrap: 'wrap',
								}}>
								<span
									style={{
										fontSize: '0.9rem',
										fontWeight: 'var(--fw-bold, 700)',
										color: 'var(--text-heading, #384971)',
									}}>
									{closure.name}
								</span>
								{past && <Chip tone='muted'>Past</Chip>}
							</span>
							<span
								style={{
									display: 'block',
									fontSize: '0.8rem',
									color: 'var(--text-muted, #7b8499)',
									marginTop: 2,
									lineHeight: 1.5,
								}}>
								{formatRange(closure.start, closure.end)}
							</span>
						</div>
					);
				})}
			</Card>)}
		</>
	);

	// Embedded in the staff Scheduling tab: no page head (that tab draws its own
	// PortalHeader) and no outer column padding (.ps-page already provides it).
	if (embedded) return <div>{body}</div>;

	return (
		<PageWrap>
			<PageHead
				title='Season calendar'
				subtitle={`${SEASON_LABEL} — recitals, breaks and key dates.`}
			/>
			{/* Customer mount only. The staff tab keeps its stacked layout, so it
			    has no view to switch between and must not grow a control that
			    implies one. */}
			<div className='cust-vtog-row'>
				<ViewToggle value={view} onChange={setView} label='Season calendar view' />
			</div>
			{body}
			{/* Customer mount only — the embedded staff tab has its own Subscribe
			    surface (Scheduling → Subscribe) with per-location feeds. */}
			<CalendarSyncCard />
		</PageWrap>
	);
};

export default SeasonCalendar;
